import cx from 'classnames';
import {type FC, useMemo, useRef} from 'react';
import './EnvelopeDisplay.sass';
import {useSelector} from 'react-redux';
import {useEnvelope} from '~/common/FabricInvitation/EventCreateDesign/components/Envelope/actions/useEnvelope';
import {useLiners} from '~/common/FabricInvitation/EventCreateDesign/components/Envelope/actions/useLiner';
import {useStamp} from '~/common/FabricInvitation/EventCreateDesign/components/Stamp/actions/useStamp';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import type {EnvelopeProps} from '~/common/FabricInvitation/EventCreateDesign/contexts/EnvelopeContext';
import {
  selectIsPremium,
  selectIsProOrPartyPass,
} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {
  selectLoaded,
  selectUsedPremiumFeature,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {EnvelopeImage} from './components/EnvelopeImage';
import {EnvelopeLiner} from './components/EnvelopeLiner';
import {EnvelopeMessage} from './components/EnvelopeMessage';
import {EnvelopeStamp} from './components/EnvelopeStamp';
import {StickersCanvasWrapper} from './components/StickersCanvasWrapper';
import {useEnvelopeNavigation} from './hooks/useEnvelopeNavigation';
import {useEnvelopeSize} from './hooks/useEnvelopeSize';

export const EnvelopeDisplay: FC<EnvelopeProps> = ({
  id,
  type,
  onLoad,
  redirectTo,
  message,
  className = '',
  showLiner = false,
  showStamp = false,
  showStickers = false,
  style = {},
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isPremium = useSelector(selectIsPremium);
  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const loaded = useSelector(selectLoaded);
  const {selectedEnvelope} = useEnvelope();
  const {selectedLiner} = useLiners();
  const {selectedStamp} = useStamp();
  const isDesktop = useIsLargishAndUp();
  const isMobile = !isDesktop;

  const {width: envelopeWidth, height: envelopeHeight} = useEnvelopeSize({
    type,
  });

  const {handleNavigation} = useEnvelopeNavigation(
    isMobile,
    redirectTo,
    redirectTo === Sections.CARD_DESIGN
  );

  const textColor = selectedEnvelope?.text_color;
  const isClickable = loaded && redirectTo;
  const linerImageUrl = selectedLiner?.image;
  const stampImageUrl = selectedStamp?.image;

  const combinedStyle = useMemo(
    () => ({
      ...style,
      width: envelopeWidth,
      height: envelopeHeight,
    }),
    [style, envelopeWidth, envelopeHeight]
  );

  const wrapperClassNames = useMemo(
    () =>
      cx(
        'envelope_image__wrapper',
        `envelope_image__wrapper__${type}`,
        {
          'envelope_image__wrapper--clickable': redirectTo,
          'envelope_image__wrapper--clickable--disabled': !loaded,
        },
        className
      ),
    [type, redirectTo, loaded, className]
  );

  if (!isPremium && !isProOrPartyPass && !usedPremiumFeature) return null;

  return (
    <div
      ref={ref}
      onClick={isClickable ? handleNavigation : undefined}
      onKeyDown={isClickable ? handleNavigation : undefined}
      className={wrapperClassNames}
      style={combinedStyle}
      role="button"
      tabIndex={0}
    >
      <div style={{display: !showStamp ? 'block' : 'none'}}>
        <EnvelopeImage imageUrl={selectedEnvelope?.blank} id={id} onLoad={onLoad} />
      </div>
      <div style={{display: showLiner ? 'block' : 'none'}}>
        <EnvelopeLiner imageUrl={linerImageUrl} />
      </div>
      <div style={{display: showStamp ? 'block' : 'none'}}>
        <EnvelopeImage imageUrl={selectedEnvelope?.image} id={id} onLoad={onLoad} />
        <EnvelopeStamp imageUrl={stampImageUrl} />
      </div>
      {message && <EnvelopeMessage message={message} textColor={textColor} />}
      {showStickers && <StickersCanvasWrapper />}
    </div>
  );
};
